/**
 * Skin-weight hygiene at the export door — the file must survive its own
 * loader bit-for-bit. three's GLTFLoader normalizes every skinned mesh's
 * weights on load (`SkinnedMesh.normalizeSkinWeights`, "to fix malformed
 * assets", #15319): scale = 1 / (|x|+|y|+|z|+|w|) in float64 over the float32
 * values, then multiply and store back as float32. Wherever the four float32
 * weights do not sum to EXACTLY 1 in that arithmetic, the loader moves them by
 * an ulp — and not idempotently: a second load moves some again. Measured on
 * the fox onesie (the reopen census, 2026-08-27): 9106 of 11322 vertices off
 * by up to 7.45e-8, a reopened export drifting 5.96e-8 from its file while
 * every one of our doors was a bit-for-bit no-op.
 *
 * Invisible on the flesh; not invisible to the law that the export → reopen
 * → export loop is the identity on the bytes we ship (#110, the census). The
 * cure is at the write: for each vertex, find ONE slot whose float32 weight
 * can be replaced so the loader's own slot-ordered float64 sum is exactly
 * 1.0, and take the candidate with the smallest change. The loader then
 * computes scale = 1 and `multiplyScalar(1)` returns each value unchanged.
 * The change is a few float32 ulps of one weight (≤ ~2e-7 absolute) —
 * numeric drift, never a paint change — the same class as the exporter's
 * unit-quaternion hygiene: applied to the live attribute and not restored.
 *
 * Why not "nudge the largest": a residual of one ulp of the largest weight
 * is several ulps of the smallest, and 1 − (the other three) need not be
 * representable at the largest weight's resolution at all. The finest-ulp
 * slot can always absorb an exact residual; trying every slot and keeping the
 * smallest move is the honest version of that.
 *
 * Fail-loud: a vertex whose weights cannot be made to sum to 1 (a zero-total
 * vertex — which the loader sets to (1,0,0,0) itself — or a spread so wide
 * the float64 sum is inexact) is reported by index, never silently left.
 *
 * Depends on: three (BufferAttribute). Depended on by: export-glb.
 */
import type { BufferAttribute, InterleavedBufferAttribute } from 'three';

export interface SkinWeightHygieneReport {
  /** Vertices whose weights were moved to an exact float32 sum of 1. */
  readonly nudged: number;
  /** Largest absolute change applied to any single weight BY THE NUDGE (after any proportional scale). */
  readonly largestNudge: number;
  /**
   * Vertices whose sum was MATERIALLY off 1 (beyond {@link EXACT_SUM_TOLERANCE})
   * and were first scaled proportionally — the loader's own arithmetic — before
   * the nudge. Not float32 residue: a paint some exporter never normalized
   * (Codex P2, #127). Counted, never silent; zero on everything our doors wrote.
   */
  readonly normalized: number;
  /** Vertices left as they were: zero total (the loader's own case) or no single-slot candidate reaches 1. */
  readonly unreachable: readonly number[];
}

/**
 * Above this the sum is not float32 residue (the census class was ≤ 7.45e-8;
 * this is two orders above it) but a materially unnormalized paint, and the
 * honest repair is the loader's: scale every slot by 1/sum, THEN make the
 * float32 sum exact. Stuffing the whole complement into one slot there would
 * change the influence ratios — (0.125 ×4) must become (0.25 ×4), not
 * (0.625, 0.125, 0.125, 0.125).
 */
export const EXACT_SUM_TOLERANCE = 1e-5;

const f32 = new DataView(new ArrayBuffer(4));
/** `steps` float32 ulps away from `x` (positive values only — weights). */
function ulpsAway(x: number, steps: number): number {
  f32.setFloat32(0, Math.fround(x));
  const bits = f32.getUint32(0) + steps;
  if (bits < 0 || bits > 0x7f7fffff) return Number.NaN;
  f32.setUint32(0, bits);
  return f32.getFloat32(0);
}

/** The loader's arithmetic: |x|+|y|+|z|+|w| in slot order, float64 over float32 values. */
function loaderSum(w: readonly number[]): number {
  let s = 0;
  for (const x of w) s += Math.abs(x);
  return s;
}

const NEIGHBOURS = 6;

/**
 * Make every vertex's float32 weights sum to exactly 1.0 in the loader's own
 * arithmetic, changing one weight per vertex by the smallest amount that
 * does it. Mutates the attribute in place; marks it for upload.
 */
export function exactSumSkinWeights(
  skinWeight: BufferAttribute | InterleavedBufferAttribute,
): SkinWeightHygieneReport {
  const n = skinWeight.count;
  const slots = skinWeight.itemSize;
  let nudged = 0;
  let largestNudge = 0;
  let normalized = 0;
  const unreachable: number[] = [];
  const w: number[] = new Array<number>(slots).fill(0);
  for (let i = 0; i < n; i += 1) {
    for (let k = 0; k < slots; k += 1) w[k] = skinWeight.getComponent(i, k);
    let sum = loaderSum(w);
    if (sum === 1) continue;
    if (!(sum > 0)) {
      unreachable.push(i); // the loader sets (1,0,0,0) for these; not this door's call
      continue;
    }
    if (Math.abs(sum - 1) > EXACT_SUM_TOLERANCE) {
      // Materially unnormalized: the loader's own repair first (proportional,
      // float64 scale, float32 store), so the ratios are preserved; the nudge
      // below then removes the residue that scale leaves.
      const scale = 1 / sum;
      for (let k = 0; k < slots; k += 1) {
        w[k] = Math.fround(w[k]! * scale);
        skinWeight.setComponent(i, k, w[k]!);
      }
      normalized += 1;
      sum = loaderSum(w);
      if (sum === 1) continue;
    }
    // For each slot: the float32 nearest to the exact complement, and its
    // neighbours; keep the hit that moves least. Zero slots stay zero — a
    // weight appearing from nothing would bind flesh to a bone it never had.
    let bestSlot = -1;
    let bestValue = 0;
    let bestMove = Infinity;
    for (let k = 0; k < slots; k += 1) {
      const original = w[k]!;
      if (!(original > 0)) continue;
      let others = 0;
      for (let j = 0; j < slots; j += 1) if (j !== k) others += Math.abs(w[j]!);
      const centre = Math.fround(1 - others);
      for (let step = -NEIGHBOURS; step <= NEIGHBOURS; step += 1) {
        const candidate = ulpsAway(centre, step);
        if (!(candidate > 0)) continue;
        w[k] = candidate;
        const hit = loaderSum(w) === 1;
        w[k] = original;
        if (!hit) continue;
        const move = Math.abs(candidate - original);
        if (move < bestMove) {
          bestMove = move;
          bestSlot = k;
          bestValue = candidate;
        }
      }
    }
    if (bestSlot < 0) {
      unreachable.push(i);
      continue;
    }
    skinWeight.setComponent(i, bestSlot, bestValue);
    nudged += 1;
    largestNudge = Math.max(largestNudge, bestMove);
  }
  if (nudged > 0 || normalized > 0) skinWeight.needsUpdate = true;
  return { nudged, largestNudge, normalized, unreachable };
}
